import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import AccountSettings from "./pages/AccountSettings";
import PortfolioAnalysis from "./pages/PortfolioAnalysis";
import "./App.css";

// CSRF defence:
// - After login the backend sets a non-HttpOnly `csrf_token` cookie.
// - This interceptor reads that cookie and echoes it in the
//   `X-CSRF-Token` header on every state-changing request. The backend
//   requires the header to equal the cookie value. An attacker on
//   another origin cannot read our cookie → cannot forge the header.
// - We also send `X-Requested-With` so cross-site fetch triggers
//   a CORS preflight (blocked by the strict allowlist).
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
axios.defaults.withCredentials = true;

function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

axios.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const token = readCookie("csrf_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = token;
    }
  }
  return config;
});

// Recovery: if a state-changing request is rejected because the CSRF
// cookie was missing/stale (common after long-idle sessions), call
// /auth/me — which now re-issues a fresh csrf_token cookie — and retry
// the original request exactly once.
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const status = error.response && error.response.status;
    const detail = error.response && error.response.data && error.response.data.detail;
    const method = (cfg.method || "get").toLowerCase();
    const isStateChange = ["post", "put", "patch", "delete"].includes(method);
    const isCsrfFail =
      status === 403 && typeof detail === "string" && detail.toLowerCase().includes("csrf");
    if (isStateChange && isCsrfFail && !cfg.__csrfRetried) {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL;
        await axios.get(`${backend}/api/auth/me`, { withCredentials: true });
        cfg.__csrfRetried = true;
        return axios(cfg);
      } catch (_e) {
        // fall through to original rejection
      }
    }
    return Promise.reject(error);
  }
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] dark:bg-[#09090B]">
        <div className="text-[#52525B] dark:text-[#A1A1AA]">Yükleniyor...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] dark:bg-[#09090B]">
        <div className="text-[#52525B] dark:text-[#A1A1AA]">Yükleniyor...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/import"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio-analysis"
            element={
              <ProtectedRoute>
                <PortfolioAnalysis />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;